import { useState } from "react";
import { motion } from "framer-motion";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import ReelPlayer from "@/components/ReelPlayer";

const EASE = [0.16, 1, 0.3, 1];

export default function Showreel() {
  const [duration, setDuration] = useState(0);

  const formatRuntime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/#showreel`;
    const shareData = {
      title: "Bite — Showreel",
      text: "Watch Bite's reel — from creative vision to AI-optimized commercial.",
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", {
        description: "Share it with anyone — it opens right at the reel.",
      });
      return;
    } catch {
      // fall through to legacy copy
    }
    try {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (!ok) throw new Error("copy failed");
      toast.success("Link copied", {
        description: "Share it with anyone — it opens right at the reel.",
      });
    } catch {
      toast.error("Couldn't copy the link", { description: url });
    }
  };

  return (
    <section id="showreel" data-testid="showreel-section" className="px-6 py-28 md:px-10 md:py-36">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-10 flex items-end justify-between md:mb-14"
        >
          <div>
            <span
              data-testid="showreel-overline"
              className="font-mono text-[10px] tracking-[0.45em] text-zinc-500"
            >
              SHOWREEL
            </span>
            <h2 className="mt-4 font-serif text-4xl tracking-tight sm:text-5xl">Bite's reel.</h2>
          </div>
          <div className="flex items-center gap-6">
            <span className="hidden font-mono text-[10px] tracking-[0.45em] text-zinc-600 sm:block">
              {duration ? `RUNTIME ${formatRuntime(duration)}` : "LOADING"}
            </span>
            <button
              type="button"
              onClick={handleShare}
              data-testid="showreel-share-button"
              className="group flex items-center gap-2.5 rounded-full border border-white/20 px-5 py-2.5 font-mono text-[10px] tracking-[0.3em] text-white transition-colors duration-300 hover:border-[#E1A730] hover:bg-[#E1A730] hover:text-black"
            >
              SHARE
              <Share2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, delay: 0.1, ease: EASE }}
        >
          <ReelPlayer
            videoUrl="https://player.vimeo.com/video/1220413061"
            poster="/assets/reel-poster.jpg"
            testIdPrefix="showreel"
            onDuration={setDuration}
            share={{
              title: "Bite — Showreel",
              text: "Watch Bite's reel — from creative vision to AI-optimized commercial.",
            }}
          />
        </motion.div>
      </div>
    </section>
  );
}
